import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { UploadOutlined, HomeOutlined, ExperimentOutlined, BookOutlined, CloseOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { dutyScheduleService, DutySchedule, DutyMember } from '../services/dutyScheduleService';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import pkg from '../../package.json';
import './QuickWorkbench.css';

dayjs.locale('zh-cn');

export interface QuickWorkbenchProps {
  /** 传入时显示右上角关闭（弹层模式） */
  onRequestClose?: () => void;
}

function formatHeaderDate(d: Date): string {
  const weekday = new Intl.DateTimeFormat('zh-CN', { weekday: 'long' }).format(d);
  const month = new Intl.DateTimeFormat('zh-CN', { month: 'long' }).format(d);
  const dayNum = d.getDate();
  return `${weekday}，${month}${dayNum}日`;
}

type BenchItem = {
  key: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
  /** 值日未完成等高亮态（设计稿 Active） */
  featured?: boolean;
  badge?: string;
};

const brandLogoSrc = `${process.env.PUBLIC_URL || ''}/brand/labmind-logo.png`;
const brandLogoFallback = `${process.env.PUBLIC_URL || ''}/favicon.svg`;

const QuickWorkbench: React.FC<QuickWorkbenchProps> = ({ onRequestClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [todayDuty, setTodayDuty] = useState<{
    schedule: DutySchedule;
    dayIndex: number;
    memberIndex: number;
    member: DutyMember;
  } | null>(null);

  const isModal = Boolean(onRequestClose);
  const versionLabel = useMemo(() => String((pkg as { version?: string }).version || '0.1.0'), []);

  useEffect(() => {
    const checkTodayDuty = async () => {
      if (!user?._id && !user?.id) return;

      try {
        const todayStr = dayjs().format('YYYY-MM-DD');
        const currentUserId = user._id || user.id;

        const schedules = await dutyScheduleService.getMyDuties({
          startDate: todayStr,
          endDate: todayStr
        });

        for (const schedule of schedules) {
          for (let dayIndex = 0; dayIndex < schedule.schedule.length; dayIndex++) {
            const day = schedule.schedule[dayIndex];
            const dayDate = dayjs(day.date).format('YYYY-MM-DD');

            if (dayDate === todayStr) {
              for (let memberIndex = 0; memberIndex < day.members.length; memberIndex++) {
                const member = day.members[memberIndex];
                const memberUserId = typeof member.userId === 'string' ? member.userId : member.userId._id;

                if (String(memberUserId) === String(currentUserId)) {
                  setTodayDuty({ schedule, dayIndex, memberIndex, member });
                  return;
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('检查当天值日失败:', error);
      }
    };

    checkTodayDuty();
  }, [user]);

  const handleUploadDutyRecord = () => {
    if (todayDuty) {
      navigate(`/lab-activities/duty-schedule`);
    }
  };

  const items: BenchItem[] = [
    {
      key: 'home',
      title: '主页',
      description: '查看系统概览与统计',
      icon: <HomeOutlined className="qw-ico" />,
      onClick: () => navigate('/dashboard')
    },
    {
      key: 'instrument-reservation',
      title: '仪器预约',
      description: '快速预约与查看排期',
      icon: <ExperimentOutlined className="qw-ico" />,
      onClick: () => navigate('/instruments')
    },
    {
      key: 'literature',
      title: '文献管理',
      description: '管理与阅读实验文献',
      icon: <BookOutlined className="qw-ico" />,
      onClick: () => navigate('/literature')
    }
  ];

  if (todayDuty) {
    const isCompleted = todayDuty.member.status === '已完成';
    const hasRecords = todayDuty.member.dutyRecords && todayDuty.member.dutyRecords.length > 0;
    const recordsCount = todayDuty.member.dutyRecords?.length || 0;

    items.splice(1, 0, {
      key: 'upload-duty-record',
      title: isCompleted ? '查看值日记录' : '上传值日记录',
      description: isCompleted
        ? hasRecords
          ? `已上传 ${recordsCount} 条记录`
          : '值日已完成'
        : '上传今天的值日记录',
      icon: <UploadOutlined className="qw-ico" />,
      onClick: handleUploadDutyRecord,
      featured: !isCompleted,
      badge: !isCompleted ? 'ACTIVE' : undefined
    });
  }

  return (
    <div className={`qw-root${isModal ? ' qw-root--modal' : ''}`}>
      {isModal && <div className="qw-accent-glow" aria-hidden />}

      <header className="qw-header">
        <div className="qw-header-text">
          <p className="qw-date">{formatHeaderDate(new Date())}</p>
          <h1 className="qw-title">快速工作台</h1>
          <p className="qw-sub">常用功能一触即达</p>
        </div>
        {onRequestClose ? (
          <button type="button" className="qw-close" onClick={onRequestClose} aria-label="关闭">
            <CloseOutlined />
          </button>
        ) : null}
      </header>

      <div className="qw-main">
        <div className="qw-grid">
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`qw-tile${item.featured ? ' qw-tile--featured' : ''}`}
              onClick={item.onClick}
              aria-label={item.title}
            >
              <div className={`qw-tile-icon${item.featured ? ' qw-tile-icon--on' : ''}`}>{item.icon}</div>
              <div className="qw-tile-body">
                <div className="qw-tile-title-row">
                  <h3 className="qw-tile-title">{item.title}</h3>
                  {item.badge ? <span className="qw-badge">{item.badge}</span> : null}
                </div>
                <p className="qw-tile-desc">{item.description}</p>
              </div>
            </button>
          ))}
        </div>

        <footer className="qw-footer">
          <div className="qw-footer-left">
            <span className="qw-footer-logo" aria-hidden>
              <img
                src={brandLogoSrc}
                alt=""
                className="qw-footer-logo-img"
                onError={(e) => {
                  const el = e.currentTarget;
                  if (el.src.includes('favicon.svg')) return;
                  el.src = brandLogoFallback;
                }}
              />
            </span>
            <span className="qw-footer-brand">LabMind Precision LIMS</span>
          </div>
          <span className="qw-footer-ver">VER {versionLabel}</span>
        </footer>
      </div>
    </div>
  );
};

export default QuickWorkbench;
